import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCandidateDrawer } from "@/components/CandidateDrawer";
import { rtwPillClass, fmtDate, daysUntil, initials } from "@/lib/cc";
import { Globe, AlertTriangle } from "lucide-react";

export default function RtwPage() {
  const [cands, setCands] = useState([]);
  const [tab, setTab] = useState("all");
  const { open } = useCandidateDrawer();

  useEffect(() => {
    api.get("/candidates").then(({ data }) => setCands(data));
  }, []);

  // Per user requirement (Feb 2026): the RTW compliance view should surface
  // candidates at the Offer or Hired stages — both are points where
  // right-to-work compliance is a hard pre-employment / ongoing-employment
  // gate.
  const RTW_STAGES = ["offer", "hired"];
  const offerStageOnly = cands.filter((c) => RTW_STAGES.includes(c.stage));

  const filtered = offerStageOnly.filter((c) => {
    if (tab === "british") return c.rtw?.nationality_status === "british";
    if (tab === "immigrant") return c.rtw?.nationality_status === "immigrant";
    return true;
  });

  const expiring = offerStageOnly.filter((c) => c.rtw?.visa_expiry && daysUntil(c.rtw.visa_expiry) <= 90 && daysUntil(c.rtw.visa_expiry) > 0);

  return (
    <div className="fade-up">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">Compliance</div>
        <h1 className="font-display text-4xl font-bold flex items-center gap-3"><Globe className="w-8 h-8" />Right to Work</h1>
        <p className="text-[var(--cc-text-soft)] mt-1">UK Right-to-Work checks for candidates at the <strong>Offer</strong> or <strong>Hired</strong> stage. Tracks British and Visa holder workflows separately. HR Admin manually verifies documents.</p>
      </div>

      <div className="flex gap-2 mb-5">
        {[{ k: "all", l: "All" }, { k: "british", l: "British / Settled" }, { k: "immigrant", l: "Visa holders" }].map((t) => (
          <button key={t.k} className={`tab-btn ${tab === t.k ? "active" : ""}`} onClick={() => setTab(t.k)} data-testid={`rtw-tab-${t.k}`}>{t.l}</button>
        ))}
      </div>

      {expiring.length > 0 && (
        <div className="cc-card p-5 mb-6 border-l-4" style={{ borderLeftColor: "var(--cc-accent)" }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-[var(--cc-accent)]" />
            <span className="font-semibold">Visa expiring in 90 days · {expiring.length}</span>
          </div>
          <div className="space-y-2">
            {expiring.map((c) => (
              <button key={c.id} onClick={() => open(c.id)} className="flex items-center justify-between w-full p-2 rounded hover:bg-[var(--cc-surface)]">
                <span className="text-sm font-medium">{c.first_name} {c.last_name}</span>
                <span className="text-xs text-[var(--cc-accent)]">{daysUntil(c.rtw.visa_expiry)} days · {fmtDate(c.rtw.visa_expiry)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">Candidate</th>
              <th className="px-5 py-3 label-eyebrow">Type</th>
              <th className="px-5 py-3 label-eyebrow">Visa Category</th>
              <th className="px-5 py-3 label-eyebrow">Expiry</th>
              <th className="px-5 py-3 label-eyebrow">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-[var(--cc-border)] hover:bg-[var(--cc-surface)] cursor-pointer" onClick={() => open(c.id)} data-testid={`rtw-row-${c.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                         style={{ background: "var(--cc-primary)" }}>{initials(c.first_name, c.last_name)}</div>
                    <div className="font-semibold">{c.first_name} {c.last_name}</div>
                  </div>
                </td>
                <td className="px-5 py-3 capitalize">{c.rtw?.nationality_status || "—"}</td>
                <td className="px-5 py-3 text-[var(--cc-text-soft)]">{c.rtw?.visa_category || "—"}</td>
                <td className="px-5 py-3">{fmtDate(c.rtw?.visa_expiry)}</td>
                <td className="px-5 py-3"><span className={`pill ${rtwPillClass(c.rtw?.status)}`}>{(c.rtw?.status || "not started").replace("_", " ")}</span></td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="5" className="text-center py-10 text-[var(--cc-text-soft)]" data-testid="rtw-empty">
                No offer-stage or hired candidates to display.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
